import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';

import Modal from '../../../../components/Modal/Modal';
import Text from '../../../../components/Text/Text';
import CustomSelect from '../../../../components/Select/CustomSelect';
import { orderedPartsStatusList } from '../../../../constants/orderedPartsStatus';
import { postVendorsList } from '../../../../constants/postVendors';
import Input from '../../../../components/Input/Input';
import patchOrderedParts from '../../../../thunks/coilovers/orderedParts/patchOrderedParts';
import Alert from '../../../../components/Alert/Alert';
import AlertType from '../../../../common/alertType';
import { clearPatchOrderedParts } from '../../../../actions/coilovers/orderedParts/patchOrderedParts';
import getBrandList from '../../../../thunks/general/getBrandList';
import getOrderedPartTypeList from '../../../../thunks/general/getOrderedPartTypeList';
import setCreatePartTypeModalState from '../../../../actions/createPartTypeModal/createPartTypeModal';
import { orderedPartsIncludeList } from '../../../../constants/orderedPartsInclude';
import { orderedTypesList } from '../../../../constants/orderedTypes';

const modalBodyClassName = 'w-[600px] overflow-auto px-[10px]';
const rowClassName = 'flex flex-row justify-between gap-[16px] mb-[16px]';
const columnClassName = 'flex-1';
const dividerClassName = 'mr-[0px]';
const labelContainerClassName = 'mb-[6px]';
const labelTextClassName = 'text-[13px]';
const inputClassName = 'py-[11px] px-[13px] font-normal text-[13px]';

const OrderedPartsEditModal = ({ visible, toggleVisible, item }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const {
    patchOrderedParts: {
      loading,
      error: patchError,
      data: patchedData
    },
    brandList,
    brandListLoading,
    getOrderedPartsList,
    getOrderedPartsListLoading
  } = useSelector((state) => ({
    patchOrderedParts: state?.patchOrderedParts,
    brandList: state?.getBrandList.data,
    brandListLoading: state?.getBrandList.loading,
    getOrderedPartsList: state?.getOrderedPartsList?.data,
    getOrderedPartsListLoading: state?.getOrderedPartsList?.loading,
  }), shallowEqual);

  useEffect(() => {
    if (Object.keys(patchedData).length) {
      enqueueSnackbar('Part update successfully', { variant: 'success' });
      dispatch(clearPatchOrderedParts());
      toggleVisible();
    }
  }, [patchedData]);

  useEffect(() => {
    if (!brandList.length && !brandListLoading) {
      dispatch(getBrandList());
    }
  }, []);

  const {
    _id,
    ordered_part_id: orderedPartId,
    ordered_part_po: orderedPartPo,
    ordered_part_status: orderedPartStatus,
    ordered_type: orderedType,
    ordered_part_included: orderedPartIncluded,
    ordered_part_type: orderedPartType,
    ordered_part_vendor: orderedVendor,
    ordered_part_brand: orderedBrand,
    ordered_part_mpn: orderedMpn,
    ordered_part_qty_in_order: orderedQty,
    ordered_part_price_in_order: orderedItemPrice,
    ordered_part_tracking: orderedTracking,
    ordered_part_price_paid: orderedPrice,
    ordered_part_note: orderedNote,
  } = item;

  const {
    values: {
      partStatus,
      partIncluded,
      partType,
      partVendor,
      partBrand,
      partMpn,
      partQty,
      partTracking,
      partPrice,
      partNote,
      itemPrice,
      partPo,
      partId,
      partOrderedType,
    },
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    setValues,
    isValid,
    errors,
    touched
  } = useFormik({
    initialValues: {
      partStatus: {},
      partIncluded: {},
      partType: {},
      partVendor: {},
      partBrand: {},
      partOrderedType: {},
      partMpn: '',
      partQty: 0,
      partTracking: '',
      partPrice: 0,
      itemPrice: 0,
      partNote: '',
      partPo: '',
      partId: '',
    },
    onSubmit: (values) => {
      dispatch(patchOrderedParts({
        _id,
        formData: {
          ordered_part_status: values?.partStatus?.value,
          ordered_part_included: values?.partIncluded?.value,
          ordered_part_type: values?.partType?.value,
          ordered_part_brand: values?.partBrand?.value,
          ...(values?.partVendor?.value && { ordered_part_vendor: values?.partVendor?.value }),
          ordered_part_mpn: values?.partMpn,
          ordered_part_qty_in_order: values?.partQty,
          ordered_type: values?.partOrderedType?.value,
          ordered_part_tracking: values?.partTracking,
          ordered_part_price_paid: values?.partPrice,
          ordered_part_note: values?.partNote,
          ordered_part_price_in_order: values?.itemPrice,
          ordered_part_po: values?.partPo,
          ordered_part_id: values?.partId,
        }
      }));
    },
    validationSchema: Yup.object({
      partStatus: Yup.object().shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      }),
      partOrderedType: Yup.object().shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      }),
      partType: Yup.object().shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      }),
      partBrand: Yup.object().shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      }),
      partMpn: Yup.string().min(3, 'Npm must be at least 3 characters')
        .required('Npm is required'),
      partPo: Yup.string().min(2, 'PO must be at least 2 characters')
        .required('PO number is required'),
      partId: Yup.number().required('Part ID number is required'),
      partQty: Yup.number().required('Part qty is required'),
      partTracking: Yup.string().min(10, 'Tracking must be at least 10 characters'),
      partPrice: Yup.number(),
      itemPrice: Yup.number(),
    })
  });

  useEffect(() => {
    setValues({
      partStatus: { label: orderedPartStatus, value: orderedPartStatus },
      partOrderedType: { label: orderedType, value: orderedType },
      partIncluded: { label: orderedPartIncluded, value: orderedPartIncluded },
      partType: { label: orderedPartType, value: orderedPartType },
      partVendor: { label: orderedVendor, value: orderedVendor },
      partBrand: { label: orderedBrand, value: orderedBrand },
      partMpn: orderedMpn,
      partQty: orderedQty,
      partTracking: orderedTracking,
      partPrice: orderedPrice,
      partNote: orderedNote,
      itemPrice: orderedItemPrice,
      partPo: orderedPartPo,
      partId: orderedPartId,
    }, true);
  }, [item]);

  useEffect(() => {
    if (!getOrderedPartsList.length) {
      dispatch(getOrderedPartTypeList());
    }
  }, []);

  return (
    <Modal
      visible={visible}
      toggleVisible={() => {
          toggleVisible();
          dispatch(clearPatchOrderedParts());
        }}
      title="Edit ordered part"
      subTitle={`id: ${partId} | po: ${partPo}`}
      onSave={handleSubmit}
      canSave={!isValid || loading}
    >
      <div className={`${modalBodyClassName} customScroll`}>
        <div className={rowClassName}>
          <div className={columnClassName}>
            <Text
              text="Part ID"
              styleContainer={labelContainerClassName}
              textStyle={labelTextClassName}
            />
            <Input
              containerStyle="d-col"
              inputStyle={inputClassName}
              type="text"
              name="partId"
              value={partId || ''}
              placeholder="Part Po"
              onBlur={handleBlur}
              onChange={handleChange}
              errorText={touched?.partId && errors?.partId}
              infoText="сhanging is not recommended"
            />
          </div>
          <span className={dividerClassName} />
          <div className={columnClassName}>
            <Text text="Part PO:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
            <Input
              containerStyle="d-col"
              inputStyle={inputClassName}
              type="text"
              name="partPo"
              value={partPo || ''}
              placeholder="Part Po"
              onBlur={handleBlur}
              onChange={handleChange}
              errorText={touched?.partPo && errors?.partPo}
            />
          </div>
          <span className={dividerClassName} />
          <div className={columnClassName}>
            <Text text="Part status:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
            <CustomSelect
              value={[partStatus]}
              options={orderedPartsStatusList}
              onChange={(data) => setFieldValue('partStatus', { ...data })}
            />
          </div>
          <span className={dividerClassName} />
          <div className={columnClassName}>
            <Text text="Part type:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
            <CustomSelect
              value={[partType]}
              options={getOrderedPartsList}
              isDisabled={getOrderedPartsListLoading}
              placeholder={getOrderedPartsListLoading ? 'Loading...' : 'Select part type'}
              onChange={(data) => setFieldValue('partType', { ...data })}
              infoText="Create type"
              onInfoClick={() => dispatch(setCreatePartTypeModalState({ visible: true }))}
            />
          </div>
        </div>
        <br />
        <div className={rowClassName}>
          <div className={columnClassName}>
            <Text text="Part vendor:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
            <CustomSelect
              value={[partVendor]}
              options={postVendorsList}
              onChange={(data) => setFieldValue('partVendor', { ...data })}
            />
          </div>
          <span className={dividerClassName} />
          <div className={columnClassName}>
            <Text text="Part brand:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
            <CustomSelect
              value={[partBrand]}
              options={brandList}
              placeholder={brandListLoading ? 'Loading...' : 'Select brand'}
              isDisabled={brandListLoading}
              isSearchable
              onChange={(data) => setFieldValue('partBrand', { ...data })}
            />
          </div>
          <span className={dividerClassName} />
          <div className={columnClassName}>
            <Text text="Include:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
            <CustomSelect
              value={[partIncluded]}
              options={orderedPartsIncludeList}
              onChange={(data) => setFieldValue('partIncluded', { ...data })}
            />
          </div>
        </div>
        <br />
        <div className={rowClassName}>
          <div className={columnClassName}>
            <Text text="Price for order:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
            <Input
              containerStyle="d-col"
              inputStyle={inputClassName}
              type="number"
              step={0.1}
              name="partPrice"
              value={`${partPrice}` || ''}
              placeholder="Part price"
              onBlur={handleBlur}
              onChange={handleChange}
              errorText={touched?.partPrice && errors?.partPrice}
            />
          </div>
          <span className={dividerClassName} />
          <div className={columnClassName}>
            <Text text="Ordered type:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
            <CustomSelect
              value={[partOrderedType]}
              options={orderedTypesList}
              onChange={(data) => setFieldValue('partOrderedType', { ...data })}
            />
          </div>
        </div>
        <div className={rowClassName}>
          <div className={columnClassName}>
            <Text text="Part NPM:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
            <Input
              containerStyle="d-col"
              inputStyle={inputClassName}
              type="text"
              name="partMpn"
              value={partMpn || ''}
              placeholder="Part mpn"
              onBlur={handleBlur}
              onChange={handleChange}
              errorText={touched?.partMpn && errors?.partMpn}
            />
          </div>
          <span className={dividerClassName} />
          <div className={columnClassName}>
            <Text text="Price per item:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
            <Input
              containerStyle="d-col"
              inputStyle={inputClassName}
              type="number"
              name="itemPrice"
              value={`${itemPrice}`}
              placeholder="Part price"
              onBlur={handleBlur}
              onChange={handleChange}
              errorText={touched?.itemPrice && errors?.itemPrice}
            />
          </div>
          <span className={dividerClassName} />
          <div className={columnClassName}>
            <Text text="Part Qty:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
            <Input
              containerStyle="d-col"
              inputStyle={inputClassName}
              type="number"
              name="partQty"
              value={`${partQty}`}
              placeholder="Part qty"
              onBlur={handleBlur}
              onChange={handleChange}
              errorText={touched?.partQty && errors?.partQty}
            />
          </div>
        </div>
        <div className={rowClassName}>
          <div className={columnClassName}>
            <Text text="Part tracking:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
            <Input
              containerStyle="d-col"
              inputStyle={inputClassName}
              type="text"
              name="partTracking"
              value={partTracking || ''}
              placeholder="Tracking number"
              onBlur={handleBlur}
              onChange={handleChange}
              errorText={touched?.partTracking && errors?.partTracking}
              multiline
            />
          </div>
        </div>
        <div className={rowClassName}>
          <div className={columnClassName}>
            <Text text="Part note:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
            <Input
              containerStyle="d-col"
              inputStyle={inputClassName}
              type="text"
              name="partNote"
              value={partNote || ''}
              placeholder="Part note"
              onBlur={handleBlur}
              onChange={handleChange}
              errorText={touched?.partNote && errors?.partNote}
              multiline
            />
          </div>
        </div>
        {patchError && <Alert text={patchError} type={AlertType.warning} />}
      </div>
    </Modal>
  );
};

OrderedPartsEditModal.propTypes = {
  toggleVisible: PropTypes.func,
  visible: PropTypes.bool,
  item: PropTypes.shape({
    _id: PropTypes.string,
    ordered_part_id: PropTypes.number,
    ordered_part_po: PropTypes.string,
    ordered_type: PropTypes.string,
    ordered_part_status: PropTypes.string,
    ordered_part_included: PropTypes.string,
    ordered_part_type: PropTypes.string,
    ordered_part_vendor: PropTypes.string,
    ordered_part_brand: PropTypes.string,
    ordered_part_mpn: PropTypes.string,
    ordered_part_qty_in_order: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ordered_part_price_in_order: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ordered_part_tracking: PropTypes.string,
    ordered_part_price_paid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ordered_part_note: PropTypes.string,
  })
};

OrderedPartsEditModal.defaultProps = {
  toggleVisible: () => {},
  visible: false,
  item: {}
};

export default OrderedPartsEditModal;
